import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { Box, Text, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import ProgressBar from '@atlaskit/progress-bar';
import FileIcon from '@atlaskit/icon/core/file';
import ImageIcon from '@atlaskit/icon/core/image';
import AudioIcon from '@atlaskit/icon/core/audio';
import VideoIcon from '@atlaskit/icon/core/video';
import CrossIcon from '@atlaskit/icon/core/cross';
import DownloadIcon from '@atlaskit/icon/core/download';
import { ErrorMessage } from '@atlaskit/form';

export type FileCardProps = {
	/**
	 * Whether the file can be deleted
	 */
	canDelete?: boolean;

	/**
	 * Whether the file can be downloaded
	 */
	canDownload?: boolean;

	/**
	 * Error message to display if there's an error
	 */
	error?: string;

	/**
	 * The name of the file to display
	 */
	fileName: string;

	/**
	 * The size of the file in bytes
	 */
	fileSize?: number;

	/**
	 * The MIME type of the file
	 */
	fileType?: string;

	/**
	 * Whether the file is currently being uploaded
	 */
	isUploading?: boolean;

	/**
	 * Callback when the file is deleted
	 */
	onDelete?: () => void;

	/**
	 * Callback when the file is downloaded
	 */
	onDownload?: () => void;

	/**
	 * Test ID for testing
	 */
	testId?: string;

	/**
	 * Upload progress (0-1) when isUploading is true
	 */
	uploadProgress?: number;
};

/**
 * Format file size in human-readable format
 */
const formatFileSize = (bytes: number): string => {
	if (bytes === 0) {
		return '0 B';
	}

	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Get file extension from filename (if included)
 */
const getFileExtension = (fileName: string): string => {
	const lastDot = fileName.lastIndexOf('.');
	return lastDot > 0 ? fileName.substring(lastDot + 1).toUpperCase() : '';
};

/**
 * Get file extension from MIME type by parsing the type
 */
const getExtensionFromMimeType = (mimeType: string): string => {
	// Extract the subtype from MIME type (e.g., "image/png" -> "png")
	const parts = mimeType.split('/');
	if (parts.length === 2) {
		return parts[1].toUpperCase();
	}
	return '';
};

/**
 * Get the appropriate icon component based on MIME type
 */
const getFileIcon = (mimeType?: string) => {
	if (!mimeType) {
		return <FileIcon label="File" />;
	}

	const type = mimeType.toLowerCase();

	if (type.startsWith('image/')) {
		return <ImageIcon label="Image file" />;
	}

	if (type.startsWith('audio/')) {
		return <AudioIcon label="Audio file" />;
	}

	if (type.startsWith('video/')) {
		return <VideoIcon label="Video file" />;
	}

	return <FileIcon label="File" />;
};

const styles = cssMap({
	card: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		height: 'auto',
		paddingTop: token('space.200', '16px'),
		paddingRight: token('space.200', '16px'),
		paddingBottom: token('space.200', '16px'),
		paddingLeft: token('space.200', '16px'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		transition: 'all 0.2s ease',
		gap: token('space.200'),
	},
	fileIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	fileInfo: {
		flex: 1,
	},
	actionButtons: {
		display: 'flex',
		alignItems: 'center',
	},
	progressBar: {
		paddingTop: token('space.100'),
	},
});

/**
 * A file card component that displays file information with actions
 */
export const FileCard = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const {
		fileName,
		fileSize,
		fileType,
		canDelete = true,
		canDownload = true,
		onDelete,
		onDownload,
		isUploading = false,
		uploadProgress = 0,
		testId,
		error,
	} = props.forgeDoc.props as FileCardProps;

	const formattedSize = fileSize ? formatFileSize(fileSize || 0) : undefined;
	const fileExtension = fileType ? getExtensionFromMimeType(fileType) : getFileExtension(fileName);

	const handleDownload = (e: React.MouseEvent) => {
		e.stopPropagation();
		onDownload?.();
	};

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		onDelete?.();
	};

	return (
		<Box
			xcss={styles.card}
			backgroundColor="elevation.surface.raised"
			onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
				e.currentTarget.style.backgroundColor = token(
					'color.background.neutral.subtle.hovered',
					'#F4F5F7',
				);
			}}
			onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
				e.currentTarget.style.backgroundColor = token('elevation.surface.raised');
			}}
			testId={testId}
		>
			{/* File icon */}
			<Box xcss={styles.fileIcon}>{getFileIcon(fileType)}</Box>

			{/* File information */}
			<Stack space="space.050" xcss={styles.fileInfo}>
				<Text>{fileName}</Text>
				{error ? (
					<ErrorMessage>{error}</ErrorMessage>
				) : isUploading ? (
					<Box xcss={styles.progressBar}>
						<ProgressBar value={uploadProgress} ariaLabel={`${fileName} is currently uploading`} />
					</Box>
				) : (
					(fileExtension || formattedSize) && (
						<Text color="color.text.subtle">
							{[fileExtension, formattedSize].filter(Boolean).join(' • ')}
						</Text>
					)
				)}
			</Stack>

			{/* Action buttons */}
			<Box xcss={styles.actionButtons}>
				{/* Download button - hide when uploading or error */}
				{canDownload && !isUploading && !error && (
					<Button appearance="subtle" onClick={handleDownload} aria-label={`download ${fileName}`}>
						<DownloadIcon label="Download" />
					</Button>
				)}
				{/* Delete button - always visible */}
				{canDelete && (
					<Button appearance="subtle" onClick={handleDelete} aria-label={`delete ${fileName}`}>
						<CrossIcon label="Delete" />
					</Button>
				)}
			</Box>
		</Box>
	);
};
